import { CorporateActionEntry } from "../../../../../services/holdings";
import { DateFormat, formatDate, formatUtcDate } from "../../../../../utils";
import { trimQuantity } from "../../../utils";
import {
  ACTION_STATE_LABELS,
  ACTION_STATE_NOTES,
  ACTION_STATE_VARIANTS,
  CORPORATE_ACTION_TYPE_LABELS,
  UNKNOWN_POSITION,
} from "./constants";
import { CorporateActionDisplay } from "./types";

// Present only on a split; built from the stored strings rather than a division since a 3-for-7 split isn't representable as a float and the exact ratio must be recognisable.
const ratioTextOf = ({ newShares, oldShares }: CorporateActionEntry) => {
  if (newShares === null || oldShares === null) {
    return null;
  }
  return `${trimQuantity(newShares)}-for-${trimQuantity(oldShares)}`;
};

// Absent until the action settles — an arrow pointing at a blank would read as a quantity of nothing.
const effectTextOf = ({ resultingQuantity }: CorporateActionEntry) => {
  if (resultingQuantity === null) {
    return null;
  }
  return `Quantity → ${trimQuantity(resultingQuantity)}`;
};

// Reconciled's second line is the settlement date, which only the entry carries; every other state falls back to its note (APPLIED has none).
// reconciledAt is a real instant, not a calendar date, so it's rendered in UTC — the zone it was stored in — to avoid naming the previous day west of it or coupling to the box's TZ.
const stateNoteOf = ({
  holdingActionState,
  reconciledAt,
}: CorporateActionEntry) => {
  if (holdingActionState === "RECONCILED" && reconciledAt !== null) {
    return `Reconciled on ${formatUtcDate(reconciledAt, DateFormat.DMY_SPACE)}`;
  }
  return ACTION_STATE_NOTES[holdingActionState];
};

// Null when the action carries neither fact — repeating the type label would only echo the badge.
const detailsTextOf = (entry: CorporateActionEntry) => {
  const parts = [ratioTextOf(entry), effectTextOf(entry)].filter(
    (part) => part !== null,
  );
  return parts.length === 0 ? null : parts.join(" · ");
};

// Built once here, not per-surface — same split tradeCommandDisplay makes. Every quantity goes through trimQuantity: these are DECIMAL(28,6) strings, and parsing to print would lose precision the ledger depends on.
export const buildCorporateActionDisplay = (
  entry: CorporateActionEntry,
): CorporateActionDisplay => ({
  dateText: formatDate(entry.effectiveDate, DateFormat.DMY_SPACE),
  typeLabel: CORPORATE_ACTION_TYPE_LABELS[entry.type],
  // Instrument is nullable on the wire though holdingId isn't; not the account-level fallback a command uses, since an action is always per-position, so a missing row is an unknown position, not account cash.
  positionText: entry.instrument?.symbol ?? UNKNOWN_POSITION,
  stateLabel: ACTION_STATE_LABELS[entry.holdingActionState],
  stateVariant: ACTION_STATE_VARIANTS[entry.holdingActionState],
  stateNote: stateNoteOf(entry),
  detailsText: detailsTextOf(entry),
});
